import {
  Utils,
  ONELINK_DEFAULTS as DEFAULTS,
  getOnelinkSocialPreviewEntry as getSocialPreviewEntry,
  getOnelinkTemplateEntry as getTemplateEntry,
  onelinkNormalizeOptionValue as normalizeOptionValue,
  ONELINK_LINK_STATUS,
  ONELINK_MEDIA_SOURCE,
  ONELINK_REDIRECT_TYPE,
  ONELINK_SOCIAL_PREVIEW,
  ONELINK_TEMPLATE,
  parseCustomParams,
} from "../../utils";

function slugifyCampaignCode(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

/** Resolve URL slug (cid) from name */
export function resolveCampaignCode(draft) {
  const name = String(draft?.name || "").trim();
  const explicit = String(draft?.campaign_code || "").trim();
  return slugifyCampaignCode(explicit || name);
}

/** Live preview — shows short URL format (actual short code assigned on save) */
export function buildTrackingLinkPreview(draft, appUuid) {
  if (!draft || !appUuid) return "";
  const root = String(Utils.baseurl || "").replace(/\/+$/, "");
  if (!root) return "";

  if (draft.short_code) {
    return `${root}/c/${draft.short_code}`;
  }

  const code = resolveCampaignCode(draft);
  if (!code) return "";
  return `${root}/c/${code}`;
}

export function campaignToDraft(c) {
  const cp = parseCustomParams(c.custom_params);
  const mediaSourceValue = normalizeOptionValue(
    ONELINK_MEDIA_SOURCE,
    cp.media_source ?? c.channel,
    DEFAULTS.mediaSourceValue
  );

  return {
    id: c.id,
    name: c.name || "",
    campaign_code: c.campaign_code || "",
    campaign_type: c.campaign_type || null,
    mediaSourceValue,
    templateValue: normalizeOptionValue(
      ONELINK_TEMPLATE,
      cp.template_id,
      DEFAULTS.templateValue
    ),
    retargeting: Boolean(cp.retargeting),
    short_code: c.short_code || "",
    attribution_window_days: c.attribution_window_days || 7,
    linkStatusValue: normalizeOptionValue(
      ONELINK_LINK_STATUS,
      c.status,
      DEFAULTS.linkStatusValue
    ),
    ad_group: c.ad_group || "",
    creative: c.creative || "",
    deep_link_value: cp.deep_link_value || "",
    deep_link_extra: cp.deep_link_extra || "",
    uri_scheme: cp.uri_scheme || "",
    socialPreviewValue: normalizeOptionValue(
      ONELINK_SOCIAL_PREVIEW,
      cp.social_preview_mode,
      DEFAULTS.socialPreviewValue
    ),
    social_preview_title: cp.social_preview_title || "",
    social_preview_description: cp.social_preview_description || "",
    social_preview_image_url: cp.social_preview_image_url || "",
    social_landing_page: Boolean(cp.social_landing_page),
    redirect_url_android: c.redirect_url_android || "",
    redirect_url_ios: c.redirect_url_ios || "",
    redirect_url_web: c.redirect_url_web || "",
    redirectAndroidValue: normalizeOptionValue(
      ONELINK_REDIRECT_TYPE,
      cp.redirect_android_type,
      DEFAULTS.redirectTypeValue
    ),
    redirectIosValue: normalizeOptionValue(
      ONELINK_REDIRECT_TYPE,
      cp.redirect_ios_type,
      DEFAULTS.redirectTypeValue
    ),
    tracking_url: c.tracking_url,
  };
}

export function buildInitialDraft(overrides = {}) {
  const mediaSourceValue = overrides.mediaSourceValue ?? DEFAULTS.mediaSourceValue;

  return {
    campaign_type: 1,
    mediaSourceValue,
    name: overrides.name || "",
    campaign_code: "",
    templateValue: DEFAULTS.templateValue,
    retargeting: false,
    short_code: "",
    attribution_window_days: 7,
    linkStatusValue: DEFAULTS.linkStatusValue,
    ad_group: "",
    creative: "",
    socialPreviewValue: DEFAULTS.socialPreviewValue,
    social_preview_title: "",
    social_preview_description: "",
    social_preview_image_url: "",
    social_landing_page: false,
    deep_link_value: "",
    deep_link_extra: "",
    uri_scheme: "",
    redirect_url_android: "",
    redirect_url_ios: "",
    redirect_url_web: "",
    redirectAndroidValue: DEFAULTS.redirectTypeValue,
    redirectIosValue: DEFAULTS.redirectTypeValue,
    ...overrides,
  };
}

/**
 * Maps form draft → API payload.
 * | UI field        | DB column      | custom_params        |
 * | Campaign name   | name           | —                    |
 * | (slug for URL)  | campaign_code  | —                    |
 * | Media source    | channel (int)  | experience, social…  |
 * | Ad group        | ad_group       | —                    |
 * | Creative        | creative       | —                    |
 */
export function draftToSavePayload(draft, appUuid, editingId) {
  const name = (draft.name || "").trim();
  const template = getTemplateEntry(draft.templateValue);
  const socialPreview = getSocialPreviewEntry(draft.socialPreviewValue);
  const campaignCode = resolveCampaignCode(draft);

  const custom_params = {
    template_id: template.value,
    retargeting: Boolean(draft.retargeting),
    deep_link_value: (draft.deep_link_value || "").trim(),
    deep_link_extra: (draft.deep_link_extra || "").trim(),
    uri_scheme: (draft.uri_scheme || "").trim(),
    social_preview_mode: socialPreview.value,
    social_preview_title: (draft.social_preview_title || "").trim(),
    social_preview_description: (draft.social_preview_description || "").trim(),
    social_preview_image_url: (draft.social_preview_image_url || "").trim(),
    social_landing_page: Boolean(draft.social_landing_page),
    redirect_android_type: draft.redirectAndroidValue ?? DEFAULTS.redirectTypeValue,
    redirect_ios_type: draft.redirectIosValue ?? DEFAULTS.redirectTypeValue,
  };

  return {
    id: editingId || draft.id,
    app_id: appUuid,
    name,
    campaign_type: draft.campaign_type ?? 1,
    channel: draft.mediaSourceValue,
    ad_group: (draft.ad_group || "").trim() || null,
    creative: (draft.creative || "").trim() || null,
    campaign_code: campaignCode,
    custom_params,
    redirect_url_android: (draft.redirect_url_android || "").trim() || null,
    redirect_url_ios: (draft.redirect_url_ios || "").trim() || null,
    redirect_url_web: (draft.redirect_url_web || "").trim() || null,
    short_code: (draft.short_code || "").trim() || null,
    attribution_window_days: draft.attribution_window_days ?? 7,
    status: draft.linkStatusValue ?? DEFAULTS.linkStatusValue,
  };
}
